import { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';
import { Activity, ChevronLeft, ChevronRight, ExternalLink, Info, Search } from 'lucide-react';

type AdminStatus = 'pending' | 'in_review' | 'completed' | 'closed';
type RankingStatus = 'pending' | 'in_progress' | 'completed';

interface ReadinessScanLead {
  id: string;
  email: string;
  websiteUrl: string;
  websiteHost: string;
  source: string;
  rankingStatus: RankingStatus;
  adminStatus?: AdminStatus;
  adminNotes?: string | null;
  createdAt?: { toDate?: () => Date };
  updatedAt?: { toDate?: () => Date };
}

interface AdminReadinessScanRequestsProps {
  showNotify?: (message: string, type?: 'success' | 'error' | 'info') => void;
}

const adminStatusLabel: Record<AdminStatus, string> = {
  pending: 'Pending',
  in_review: 'In Review',
  completed: 'Completed',
  closed: 'Closed',
};

const adminStatusColorClass: Record<AdminStatus, string> = {
  pending: 'bg-blue-100 text-blue-700',
  in_review: 'bg-amber-100 text-amber-700',
  completed: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-gray-100 text-gray-600',
};

const rankingStatusLabel: Record<RankingStatus, string> = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
};

export function AdminReadinessScanRequests({
  showNotify,
}: AdminReadinessScanRequestsProps): JSX.Element {
  const [leads, setLeads] = useState<ReadinessScanLead[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<AdminStatus | 'all'>('all');
  const [search, setSearch] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [adminNotesDraft, setAdminNotesDraft] = useState('');
  const [adminStatusDraft, setAdminStatusDraft] = useState<AdminStatus>('pending');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const q = query(
      collection(db, 'artifacts', appId, 'public', 'data', 'leads'),
      orderBy('createdAt', 'desc')
    );
    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const records = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter(
            (r): r is ReadinessScanLead =>
              (r as ReadinessScanLead).source === 'footer_federal_readiness_scan'
          );
        setLeads(records);
        setLoading(false);
      },
      (error) => {
        console.error(
          '[AdminReadinessScanRequests] Failed to subscribe to leads collection. Admin list cannot refresh:',
          error
        );
        showNotify?.('Failed to load Federal Readiness Scan requests.', 'error');
        setLoading(false);
      }
    );
    return unsubscribe;
  }, [showNotify]);

  const filtered = useMemo(() => {
    const norm = search.trim().toLowerCase();
    return leads.filter((r) => {
      const effectiveStatus: AdminStatus = r.adminStatus ?? 'pending';
      const passStatus = statusFilter === 'all' ? true : effectiveStatus === statusFilter;
      if (!passStatus) return false;
      if (!norm) return true;
      const blob = [r.email, r.websiteUrl, r.websiteHost].join(' ').toLowerCase();
      return blob.includes(norm);
    });
  }, [leads, search, statusFilter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, currentPage, pageSize]);

  const selected = useMemo(
    () => filtered.find((r) => r.id === selectedId) ?? paginated[0] ?? null,
    [filtered, selectedId, paginated]
  );

  useEffect(() => {
    if (!selected) {
      setAdminNotesDraft('');
      setAdminStatusDraft('pending');
      return;
    }
    setAdminNotesDraft(selected.adminNotes ?? '');
    setAdminStatusDraft(selected.adminStatus ?? 'pending');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected?.id]);

  const formatDate = (ts: ReadinessScanLead['createdAt']) => {
    if (!ts || typeof ts !== 'object' || typeof (ts as { toDate?: () => Date }).toDate !== 'function')
      return '—';
    return (ts as { toDate: () => Date }).toDate().toLocaleString();
  };

  const handleSaveAdmin = async () => {
    if (!selected) return;
    setIsUpdating(true);
    try {
      await updateDoc(
        doc(db, 'artifacts', appId, 'public', 'data', 'leads', selected.id),
        {
          adminStatus: adminStatusDraft,
          adminNotes: adminNotesDraft.trim() || null,
          updatedAt: serverTimestamp(),
        }
      );
      showNotify?.(`Lead for ${selected.websiteHost} updated.`, 'success');
    } catch (error) {
      console.error(
        '[AdminReadinessScanRequests] Failed to update lead:',
        selected.id,
        error
      );
      showNotify?.('Unable to update lead. Please try again.', 'error');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading) {
    return <div className="text-gray-600">Loading Federal Readiness Scan requests...</div>;
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Activity size={20} className="text-blue-600" />
          <h2 className="text-xl font-bold text-gray-900">Federal Readiness Scan Requests</h2>
        </div>
        <p className="text-sm text-gray-600">
          Manage website ranking requests from the customer dashboard and historical
          footer submissions.
        </p>
      </div>

      {/* Visibility note */}
      <div className="flex items-start gap-2.5 rounded-xl border border-blue-100 bg-blue-50 px-4 py-3 text-sm text-blue-800">
        <Info size={16} className="shrink-0 mt-0.5 text-blue-500" />
        <span>
          New scan requests are submitted from the customer dashboard (Federal
          Readiness Scan). The public footer form for scans has been removed.
        </span>
      </div>

      {/* Filters */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by website, email..."
              className="w-full rounded-lg border border-gray-300 pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AdminStatus | 'all')}
            className="rounded-lg border border-gray-300 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All statuses</option>
            <option value="pending">Pending</option>
            <option value="in_review">In Review</option>
            <option value="completed">Completed</option>
            <option value="closed">Closed</option>
          </select>
          <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700">
            <span className="font-semibold">{filtered.length}</span> request(s)
          </div>
        </div>
      </div>

      {/* List + Detail */}
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.2fr),minmax(0,1fr)] gap-4">
        {/* List */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Website
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                    Submitted
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-12 text-sm text-center text-gray-500">
                      <Activity size={32} className="mx-auto mb-3 text-gray-300" />
                      <p className="font-medium text-gray-600">No Federal Readiness Scan requests yet</p>
                      <p className="mt-1 text-xs text-gray-400">
                        Requests will appear here when users submit a scan from the customer
                        dashboard (or from older footer submissions).
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginated.map((record) => {
                    const effectiveStatus: AdminStatus = record.adminStatus ?? 'pending';
                    const active = (selected?.id ?? selectedId ?? paginated[0]?.id) === record.id;
                    return (
                      <tr
                        key={record.id}
                        onClick={() => setSelectedId(record.id)}
                        className={`cursor-pointer transition-colors ${
                          active ? 'bg-blue-50/70' : 'hover:bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-3 align-top">
                          <p className="text-sm font-semibold text-gray-900">{record.websiteHost}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[160px]">{record.websiteUrl}</p>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top">
                          {record.email}
                        </td>
                        <td className="px-4 py-3 align-top">
                          <span
                            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${adminStatusColorClass[effectiveStatus]}`}
                          >
                            {adminStatusLabel[effectiveStatus]}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-xs text-gray-600 align-top whitespace-nowrap">
                          {formatDate(record.createdAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Detail panel */}
        <div className="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
          {selected ? (
            <>
              {/* Website info */}
              <div>
                <h3 className="text-lg font-bold text-gray-900">{selected.websiteHost}</h3>
                <a
                  href={selected.websiteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline mt-0.5"
                >
                  {selected.websiteUrl}
                  <ExternalLink size={12} />
                </a>
              </div>

              {/* Fields */}
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div>
                  <p className="font-semibold text-gray-700">Email</p>
                  <p className="text-gray-900">{selected.email}</p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Ranking Status</p>
                  <p className="text-gray-900 capitalize">
                    {rankingStatusLabel[selected.rankingStatus as RankingStatus] ?? selected.rankingStatus}
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-gray-700">Submitted</p>
                  <p className="text-gray-900">{formatDate(selected.createdAt)}</p>
                </div>
                {selected.updatedAt && (
                  <div>
                    <p className="font-semibold text-gray-700">Last Updated</p>
                    <p className="text-gray-900">{formatDate(selected.updatedAt)}</p>
                  </div>
                )}
              </div>

              {/* Admin status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Admin Status
                </label>
                <select
                  value={adminStatusDraft}
                  onChange={(e) => setAdminStatusDraft(e.target.value as AdminStatus)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="in_review">In Review</option>
                  <option value="completed">Completed</option>
                  <option value="closed">Closed</option>
                </select>
              </div>

              {/* Admin notes */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Admin Notes
                </label>
                <textarea
                  rows={4}
                  value={adminNotesDraft}
                  onChange={(e) => setAdminNotesDraft(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Internal notes, follow-up actions, scan findings..."
                />
              </div>

              {/* Save */}
              <button
                type="button"
                onClick={handleSaveAdmin}
                disabled={isUpdating}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
              >
                {isUpdating ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center text-gray-500">
              <Activity size={32} className="mb-3 text-gray-300" />
              <p className="text-sm">Select a request to view details and update status.</p>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {filtered.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">
              Showing{' '}
              <span className="font-medium text-gray-900">
                {(currentPage - 1) * pageSize + 1}–
                {Math.min(currentPage * pageSize, filtered.length)}
              </span>{' '}
              of <span className="font-medium text-gray-900">{filtered.length}</span>{' '}
              request{filtered.length !== 1 ? 's' : ''}
            </span>
            <label className="flex items-center gap-2 text-sm text-gray-600">
              Rows per page
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </label>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <ChevronLeft size={16} />
              Previous
            </button>
            <span className="text-sm text-gray-600">
              Page <span className="font-medium text-gray-900">{currentPage}</span> of{' '}
              <span className="font-medium text-gray-900">{totalPages}</span>
            </span>
            <button
              type="button"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              className="inline-flex items-center gap-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
